import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useProjectWithUsers } from "@/hooks/useProjectWithUsers";
import { useViews } from "@/components/views/hooks/useViews";
import { createDefaultFilters } from "./useFilterOperations";
import { Project } from "@/utils/types";
import { FilterState } from "@/components/ProjectList/types";
import { useSearchParams } from "react-router";
import {
  deserializeFiltersFromParams,
  serializeFiltersToParams,
  getDefaultFilters,
  hasActiveFilters,
} from "@/utils/filterUrlUtils";

interface UseViewManagementProps {
  selectedViewId?: string;
  onViewSelect?: (viewId: string) => void;
  project?: Project | null;
}

export const useViewManagement = ({
  selectedViewId,
  onViewSelect,
  project,
}: UseViewManagementProps) => {
  const currentProject = useProjectWithUsers();
  const projectToUse = project || currentProject;
  const [searchParams, setSearchParams] = useSearchParams();
  const { addView, getViews } = useViews();

  const filters = useMemo(() => {
    const parsedFilters = deserializeFiltersFromParams(searchParams);
    if (!parsedFilters) return getDefaultFilters();

    return {
      ...getDefaultFilters(),
      ...parsedFilters,
    };
  }, [searchParams]);

  const [isCreatingView, setIsCreatingView] = useState(false);
  const [viewName, setViewName] = useState("");
  const [viewType, setViewType] = useState<"grid" | "list">("grid");

  const projectViews = useMemo(
    () => getViews(projectToUse?.id || ""),
    [getViews, projectToUse?.id],
  );

  const allViews = useMemo(() => projectViews, [projectViews]);
  const isCreateMode = selectedViewId === "create-new";
  const hasSavedViews = projectViews.length > 0;

  const currentView = useMemo(() => {
    if (selectedViewId === "create-new") return null;
    return allViews.find((v) => v.id === selectedViewId) || null;
  }, [selectedViewId, allViews]);

  const lastAppliedViewId = useRef<string | null>(null);

  useEffect(() => {
    if (!currentView || !projectToUse?.id) return;
    if (lastAppliedViewId.current === currentView.id) return;

    lastAppliedViewId.current = currentView.id;

    // Use setTimeout to ensure handleViewSelect's update has been applied first
    setTimeout(() => {
      setSearchParams((prev) => {
        return serializeFiltersToParams(
          prev,
          currentView.filters || getDefaultFilters(),
        );
      });
    }, 0);
  }, [currentView, projectToUse?.id, setSearchParams]);

  useEffect(() => {
    if (isCreateMode) {
      setIsCreatingView(true);
      if (projectToUse?.id) {
        setSearchParams(
          (prev) => {
            return serializeFiltersToParams(prev, getDefaultFilters());
          },
          { replace: true },
        );
      }
    } else if (selectedViewId && selectedViewId !== "create-new") {
      setIsCreatingView(false);
    }
  }, [isCreateMode, selectedViewId, projectToUse?.id, setSearchParams]);

  const handleSaveView = useCallback(async () => {
    if (!viewName.trim() || !projectToUse?.id) return;

    const newView = await addView(projectToUse.id, {
      name: viewName.trim(),
      filters: filters || createDefaultFilters(),
      isDefault: false,
    });

    if (newView) {
      setViewName("");
      setIsCreatingView(false);
      onViewSelect?.(newView.id);
    }
  }, [viewName, projectToUse?.id, filters, addView, onViewSelect]);

  const handleCancelCreate = useCallback(() => {
    setIsCreatingView(false);
    setViewName("");

    if (onViewSelect && allViews.length > 0) {
      onViewSelect(allViews[0].id);
    }
  }, [onViewSelect, allViews]);

  const handleClearFilters = useCallback(() => {
    if (!projectToUse?.id) return;
    setSearchParams((prev) => {
      return serializeFiltersToParams(prev, getDefaultFilters());
    });
  }, [projectToUse?.id, setSearchParams]);

  const handleUpdateFilter = useCallback(
    (filterType: string, value: string | number, condition?: string) => {
      if (!projectToUse?.id || !filters || !condition) return;

      const updatedFilters = {
        ...filters,
        conditions: {
          ...filters.conditions,
          [filterType]: condition,
        },
      };

      setSearchParams((prev) => {
        return serializeFiltersToParams(prev, updatedFilters);
      });
    },
    [projectToUse?.id, filters, setSearchParams],
  );

  const updateFilters = useCallback(
    (newFilters: Partial<FilterState>) => {
      if (!projectToUse?.id || !filters) return;

      const updatedFilters = {
        ...filters,
        ...newFilters,
      };

      setSearchParams((prev) => {
        return serializeFiltersToParams(prev, updatedFilters);
      });
    },
    [projectToUse?.id, filters, setSearchParams],
  );

  const hasFilters = useMemo(() => hasActiveFilters(filters), [filters]);

  return {
    // State
    isCreatingView,
    viewName,
    viewType,
    currentView,
    allViews,
    hasSavedViews,
    hasActiveFilters: hasFilters,
    filters,
    currentProject: projectToUse,

    // Setters
    setViewName,
    setViewType,
    setIsCreatingView,

    // Handlers
    handleSaveView,
    handleCancelCreate,
    handleClearFilters,
    handleUpdateFilter,
    updateFilters,
  };
};
